import { humanDuration } from '../lib/duration';
import { formatBytes, formatCount, formatPercent, type StorageEstimate } from '../lib/storage';
import CascadeDiagram from './CascadeDiagram';
import { BreakdownChart, GrowthChart } from './Charts';

interface Props {
  estimate: StorageEstimate;
}

export default function SavingsPanel({ estimate }: Props) {
  const { raw, levels } = estimate;

  return (
    <section className="panel" aria-labelledby="savings-heading" data-testid="savings">
      <header>
        <h2 id="savings-heading">Storage estimate</h2>
        <span className="panel-note">
          {estimate.bytesPerPoint} B/point · {estimate.horizonDays}-day horizon
        </span>
      </header>
      <div className="panel-body">
        <dl className="stats">
          <div className="stat">
            <dt>Raw only</dt>
            <dd data-testid="stat-raw-only">
              {formatBytes(estimate.rawOnlyBytes)}
              <small>raw kept for the full {estimate.horizonDays} days</small>
            </dd>
          </div>
          <div className="stat">
            <dt>Tiered</dt>
            <dd data-testid="stat-tiered">
              {formatBytes(estimate.tieredBytes)}
              <small>raw at {humanDuration(raw.retentionDays * 86400)} plus every rollup</small>
            </dd>
          </div>
          <div className="stat headline">
            <dt>Saved</dt>
            <dd data-testid="stat-saved">
              {formatPercent(estimate.savedFraction)}
              <small>{formatBytes(estimate.savedBytes)} at steady state</small>
            </dd>
          </div>
          <div className="stat">
            <dt>Ingest</dt>
            <dd>
              {formatCount(raw.pointsPerDay)}
              <small>raw points per day</small>
            </dd>
          </div>
        </dl>

        <CascadeDiagram estimate={estimate} />

        <div className="table-scroll" style={{ marginTop: 18 }}>
          <table>
            <caption>
              Per-bucket breakdown. &ldquo;Resident&rdquo; is the volume once the bucket has filled
              its retention window and expiry balances ingest.
            </caption>
            <thead>
              <tr>
                <th scope="col">Bucket</th>
                <th scope="col">Resolution</th>
                <th scope="col">Series</th>
                <th scope="col">Points/day</th>
                <th scope="col">Retention</th>
                <th scope="col">Resident</th>
              </tr>
            </thead>
            <tbody>
              {levels.map((level, i) => (
                <tr key={level.bucket + i}>
                  <th scope="row" style={{ fontFamily: 'ui-monospace, Menlo, monospace' }}>
                    {level.bucket}
                  </th>
                  <td>{humanDuration(level.resolutionSeconds)}</td>
                  <td>{formatCount(level.seriesCount)}</td>
                  <td>{formatCount(level.pointsPerDay)}</td>
                  <td>
                    {level.retentionClamped ? '∞' : humanDuration(level.retentionDays * 86400)}
                  </td>
                  <td>{formatBytes(level.steadyStateBytes)}</td>
                </tr>
              ))}
              <tr>
                <th scope="row">Total</th>
                <td colSpan={4} />
                <td style={{ fontWeight: 700 }}>{formatBytes(estimate.tieredBytes)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <details className="math" data-testid="math">
          <summary>Show the arithmetic</summary>
          <div>
            <p>
              Nothing here is fitted or hidden. Every figure above comes out of three lines of
              arithmetic applied per bucket:
            </p>
            <div className="formula">
              {'points/day = (86 400 / resolution_seconds) × series\n'}
              {`bytes/day  = points/day × ${estimate.bytesPerPoint}\n`}
              {'resident   = bytes/day × retention_days'}
            </div>
            <p>
              <strong>Series counts follow the cascade.</strong> The raw bucket carries every
              combination of every tag, multiplied by the field count:{' '}
              <code className="inline-code">{formatCount(raw.seriesCount)}</code> series. A rollup
              only carries the tags you kept — but each aggregate function in a tier writes its own
              series, and that factor compounds at every hop, so a three-aggregate minute tier
              feeding a two-aggregate hour tier writes six series for every input series. That
              compounding is usually why a rollup bucket costs more than expected.
            </p>
            <p>
              <strong>The baseline is deliberately generous to raw.</strong> &ldquo;Raw only&rdquo;
              prices keeping raw data at full resolution for the entire {estimate.horizonDays}-day
              horizon — the thing the cascade exists to avoid. The tiered figure is the raw bucket
              at its own shorter retention plus every rollup bucket at theirs. Infinite retention (
              <code className="inline-code">0s</code>) is priced at the horizon, since an unbounded
              number is not a useful comparison.
            </p>
            <p>
              <strong>What the model does not capture.</strong> It ignores the WAL, index and
              tombstone overhead, series that report intermittently (unless{' '}
              <code className="inline-code">createEmpty</code> is on, real gaps make the estimate
              conservative), replication factor, and shard-group boundary effects — a bucket only
              expires whole shard groups, so real usage overshoots retention slightly. Treat the
              result as a well-founded order of magnitude for capacity planning, not an invoice.
            </p>
            <p>
              Worked example for <code className="inline-code">{raw.bucket}</code>:{' '}
              <code className="inline-code">
                86 400 / {raw.resolutionSeconds} × {formatCount(raw.seriesCount)} ={' '}
                {formatCount(raw.pointsPerDay)}
              </code>{' '}
              points per day, ×{estimate.bytesPerPoint} B ={' '}
              <code className="inline-code">{formatBytes(raw.bytesPerDay)}</code> per day, ×{' '}
              {Math.round(raw.retentionDays)} days ={' '}
              <code className="inline-code">{formatBytes(raw.steadyStateBytes)}</code> resident.
            </p>
          </div>
        </details>

        <div className="charts-grid" style={{ marginTop: 22 }} data-testid="charts">
          <GrowthChart estimate={estimate} />
          <BreakdownChart estimate={estimate} />
        </div>
      </div>
    </section>
  );
}
